/**
 * IdeaOfDay
 *
 * The proactive suggestion band on the recipes home screen: four recipes, each
 * answering a different need (quickest / uses your fridge / never tried / sure
 * thing), with the reason spelled out on every card.
 *
 * The picks come from `buildFanSuggestions` — the same angle engine the weekly
 * planner's "Remplacer ce plat" fan uses — seeded with `dailySeed` so the row is
 * stable for the whole local day and differs tomorrow.
 */

import React from 'react';
import { View, Text, StyleSheet, ScrollView, ViewStyle, TextStyle } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';
import type { FanSuggestion } from '../../services/fanSuggestionsService';
import type { PersonalRecipeIndexEntry } from '../../services/personalRecipeIndex';
import { ACCENT_BY_ANGLE, angleLabelKey } from './ideaAngles';
import RecipeShelfCard from './RecipeShelfCard';

interface IdeaOfDayProps {
  suggestions: FanSuggestion<PersonalRecipeIndexEntry>[];
  ratingOf: (recipeId: string) => number | undefined;
  onPressRecipe: (recipe: PersonalRecipeIndexEntry) => void;
}

export const IdeaOfDay: React.FC<IdeaOfDayProps> = ({
  suggestions,
  ratingOf,
  onPressRecipe,
}) => {
  const { t } = useTranslation();

  if (suggestions.length === 0) return null;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Feather name="zap" size={15} color={Paprika.ai} />
        <Text style={styles.title}>{t('recipes.ideaOfDay.title')}</Text>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {suggestions.map((s) => (
          <RecipeShelfCard
            key={s.angleKey}
            recipe={s.recipe}
            personalRating={ratingOf(s.recipe.id)}
            onPress={() => onPressRecipe(s.recipe)}
            reason={{
              label: t(angleLabelKey(s.angleKey)),
              text: t(s.reasonKey, s.reasonParams),
              accent: ACCENT_BY_ANGLE[s.angleKey] ?? Paprika.ai,
            }}
          />
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  // The AI tint alone separates the band from `background` — DESIGN.md forbids
  // 1px rules between sections, so there is no border here.
  container: {
    backgroundColor: Paprika.aiTint,
    paddingTop: 16,
    paddingBottom: 18,
  } as ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: 18,
    marginBottom: 12,
  } as ViewStyle,
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 19,
    color: Paprika.ai,
    letterSpacing: -0.3,
  } as TextStyle,
  scrollContent: {
    paddingHorizontal: 18,
    gap: 13,
  } as ViewStyle,
});

export default IdeaOfDay;
